import api from '../../api'

// Services
import { EventBus } from '../../services/eventBusService'

// Store
import store from '../index'

// Utilities
import isEmpty from '../../utility/isEmpty'
import Language from '../../utility/languageUtil'
import Logger from '../../utility/loggerUtil'
import Preference from '../../utility/preferenceUtil'
import Wait from '../../utility/waitUtil'

import { BrandEnum, ServiceBrandEnum, KeyConstants, SettingsConstants } from '../../constants'

export default {
  /**
   * Gets the loans for the current customer
   * @param {JSON} context The context object for the module
   * @param {{brand: BrandEnum, [maxResultsPerCategory]: Number}} params Params for the loans call
   */
  async getLoans (context, params) {
    try {
      context.commit('resetLoans')

      const paramBrand = params?.brand || BrandEnum.None
      let serviceBrand = ServiceBrandEnum.titlemax
      for (const key in ServiceBrandEnum) {
        if (ServiceBrandEnum[key] === paramBrand) {
          serviceBrand = key
          break
        }
      }
      const loansResponse = await api.loan.getLoanSummaries(serviceBrand, params?.maxResultsPerCategory, params?.loanId)
      const { loans, reapplyInfo, hasMoreOpenLoans, hasMoreClosedLoans, pendingRefinance, pendingReapply } = loansResponse

      Logger.info('Got Loan summaries')
      Logger.debug(loans)

      if (loans.length > 1) {
        context.commit('display/setMultipleAccounts', true, { root: true })
      } else {
        context.commit('display/setMultipleAccounts', false, { root: true })
      }

      context.commit('setLoans', loans)
      context.commit('setReapplyInfo', reapplyInfo)
      context.commit('setDashboardSeeMoreInfo', { hasMoreOpenLoans, hasMoreClosedLoans })
      context.commit('setPendingRefiInfo', pendingRefinance)
      context.commit('setPendingReapplyInfo', pendingReapply)
    } catch (e) {
      throw new Error(`${Language.$t('global.get_loans_query_error')}: ${e.message}`)
    }
  },

  async prepareLoan (context, params) {
    try {
      Wait.waitStart('loans/prepareLoan')
      let details = params.loan

      if (isEmpty(details)) {
        const loanResponse = await api.loan.getLoanDetail(params.loanHeader, params.lmsId, params.storeId).catch(e => {
          Logger.error('Error getting loan details')
          Logger.debug(e)
          return api.loan.getLoanDetail(params.loanHeader, params.lmsId, params.storeId)
        })

        if (isEmpty(loanResponse)) {
          throw new Error('Error fetching latest loan data.')
        }

        details = loanResponse.loan
      }

      let preventNoSsnPay = false

      if (!isEmpty(details)) {
        if (!details.paidOff) { // Cosigner info below is only used for refinances, paid off loans don't need it
          // Check if customer or cosigner does not have SSN
          let noAltSsn = false

          if (details.cosignerId) {
            const coSignInfo = await getCosignerDetails(details.lmsId, details.loanHeader, details.storeId)

            details.alternateName = coSignInfo.customerName
            details.alternateLinked = !isEmpty(coSignInfo.registeredProfile)
            if (details.refinanceEligible === true) {
              noAltSsn = isEmpty(coSignInfo) || (!isEmpty(coSignInfo) && isEmpty(coSignInfo.displaySsn))
            }
          }

          if (context.rootState.session.allowNoSsnRefi === false && details.refinanceEligible === true && (isEmpty(context.rootState.customer.displaySsn) || noAltSsn)) {
            details.payable = false
            preventNoSsnPay = true
          }
        }

        if (!isEmpty(details.storeId)) {
          const storeInfo = context.rootGetters['store/getStoreInfo'](details.storeId)

          if (isEmpty(storeInfo)) {
            await getStoreDetails(details.storeId)
          }
        }

        context.commit('updateLoan', details)
      }

      return {
        loan: details,
        preventNoSsnPay
      }
    } catch (error) {
      Logger.error('Error preparing loan')
      Logger.debug(error)
      throw error
    } finally {
      Wait.waitEnd('loans/prepareLoan')
    }
  },

  async getApplications (context, params) {
    try {
      const applications = await api.loan.getLoanApplicationStatus(params?.maxResults)
        .catch(e => {
          Logger.error('Error on api.loan.getLoanApplicationStatus')
          Logger.debug(e)
          return api.loan.getLoanApplicationStatus(params?.maxResults)
        })

      EventBus.$emit('setActiveElement') // Once applications are assigned, if one is in progress a modal will show, this attempt to fix an IE 11 modal display bug

      context.commit('updateApplications', applications)
      context.commit('setDashboardSeeMoreInfo', { hasMoreDeclinedResults: applications.hasMoreDeclinedResults })
    } catch (e) {
      Logger.error('Error getting applications')
      Logger.debug(e)
      throw e
    }
  },

  async getRiskInfo (context, data) {
    try {
      const riskInfo = await api.loan.getRiskInfo(data.isAddon, data.loanId, data.migrateProduct)
      if (!isEmpty(riskInfo)) {
        context.commit('setRateChangeModal', riskInfo.rateChanged)
        context.commit('setRefinanceDenial', riskInfo.refinanceDenied)
        context.commit('payment/setPaymentData', {
          riskCheck: riskInfo.rateChanged || riskInfo.creditLimitChanged
        }, { root: true })

        await context.dispatch('manageCreditLimitChange', {
          riskInfo
        })
      }
    } catch (e) {
      Logger.error('Error getting risk info.')
      Logger.debug(e)
      throw e
    }
  },

  async manageCreditLimitChange (context, data) {
    try {
      if (!isEmpty(data) && data.hasAcknowledged === true) {
        Preference.delete(KeyConstants.creditLimitChange)
      }

      if (!isEmpty(data) && !isEmpty(data.riskInfo) && data.riskInfo.creditLimitChanged === true) {
        await Preference.set(KeyConstants.creditLimitChange, true)
      }

      const hasCreditLimitChanged = await Preference.get(KeyConstants.creditLimitChange, null) || false
      context.commit('setCreditLimitModal', hasCreditLimitChanged)
    } catch (e) {
      Logger.error('Error checking risk changes')
      Logger.debug(e)
      throw e
    }
  }
}

const getCosignerDetails = async (lmsId, loanHeader, storeId) => {
  try {
    const cosignerInfo = await store.cache.dispatch('customer/getCustomerInfo', { lmsId, loanHeader, storeId }, {
      timeout: SettingsConstants.shortCacheTimeout
    })
    return cosignerInfo
  } catch (e) {
    Logger.error('Error on getCosignerDetails')
    Logger.debug(e)

    store.cache.delete('customer/getCustomerInfo', { lmsId, loanHeader, storeId })
    throw e
  }
}

const getStoreDetails = async (storeId) => {
  try {
    await store.cache.dispatch('store/getStoreDetails', storeId, {
      timeout: SettingsConstants.longCacheTimeout
    })
  } catch (e) {
    Logger.error('Error on getStoreDetails')
    Logger.debug(e)

    store.cache.delete('store/getStoreDetails', storeId)
    throw e
  }
}
